import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	CheckboxOption,
	FilterBarContainer,
	MoreFiltersButton,
	MultiSelectButton,
	MultiSelectContainer,
	MultiSelectDropdown,
	QuickFiltersRow,
	SearchWrapper,
	StatusPill,
	StatusPillGroup,
} from './styled';
import {
	ELECTION_TYPES,
	type ElectionFilters,
	type ElectionStatus,
	type ElectionType,
	STATUS_OPTIONS,
} from './useElectionFilters';

interface FilterBarProps {
	filters: ElectionFilters;
	advancedFilterCount: number;
	isAdvancedOpen: boolean;
	onSearchChange: (value: string) => void;
	onToggleElectionType: (type: ElectionType) => void;
	onClearElectionTypes: () => void;
	onSelectAllElectionTypes: () => void;
	onStatusChange: (status: ElectionStatus | null) => void;
	onToggleAdvanced: () => void;
}

const SearchIcon = () => (
	<svg
		width='18'
		height='18'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<circle
			cx='11'
			cy='11'
			r='8'
		/>
		<line
			x1='21'
			y1='21'
			x2='16.65'
			y2='16.65'
		/>
	</svg>
);

const ChevronIcon = () => (
	<svg
		className='chevron'
		width='12'
		height='12'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<polyline points='6 9 12 15 18 9' />
	</svg>
);

const SettingsIcon = () => (
	<svg
		width='16'
		height='16'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<circle
			cx='12'
			cy='12'
			r='3'
		/>
		<path d='M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z' />
	</svg>
);

export const FilterBar: React.FC<FilterBarProps> = ({
	filters,
	advancedFilterCount,
	isAdvancedOpen,
	onSearchChange,
	onToggleElectionType,
	onClearElectionTypes,
	onSelectAllElectionTypes,
	onStatusChange,
	onToggleAdvanced,
}) => {
	const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsTypeDropdownOpen(false);
			}
		};

		if (isTypeDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isTypeDropdownOpen]);

	// Close dropdown on escape
	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsTypeDropdownOpen(false);
			}
		};

		if (isTypeDropdownOpen) {
			document.addEventListener('keydown', handleEscape);
		}

		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, [isTypeDropdownOpen]);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onSearchChange(e.target.value);
		},
		[onSearchChange]
	);

	const handleTypeCheckboxChange = useCallback(
		(type: ElectionType) => () => {
			onToggleElectionType(type);
		},
		[onToggleElectionType]
	);

	const handleStatusClick = useCallback(
		(status: ElectionStatus | null) => () => {
			onStatusChange(status);
		},
		[onStatusChange]
	);

	const selectedTypeCount = filters.electionTypes.length;

	return (
		<FilterBarContainer>
			<SearchWrapper>
				<span className='search-icon'>
					<SearchIcon />
				</span>
				<input
					type='text'
					placeholder='Search elections...'
					value={filters.searchQuery}
					onChange={handleSearchChange}
					aria-label='Search elections by name or location'
				/>
			</SearchWrapper>

			<QuickFiltersRow>
				{/* Election Type Multi-Select */}
				<MultiSelectContainer ref={dropdownRef}>
					<MultiSelectButton
						type='button'
						className={isTypeDropdownOpen ? 'open' : ''}
						onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
						aria-expanded={isTypeDropdownOpen}
						aria-haspopup='listbox'
					>
						Election Type
						{selectedTypeCount > 0 && <span className='badge'>{selectedTypeCount}</span>}
						<ChevronIcon />
					</MultiSelectButton>

					{isTypeDropdownOpen && (
						<MultiSelectDropdown
							role='listbox'
							aria-label='Election types'
						>
							{ELECTION_TYPES.map((type) => (
								<CheckboxOption key={type.value}>
									<input
										type='checkbox'
										checked={filters.electionTypes.includes(type.value)}
										onChange={handleTypeCheckboxChange(type.value)}
										aria-label={type.label}
									/>
									<span>{type.label}</span>
								</CheckboxOption>
							))}
							<div className='dropdown-actions'>
								<button
									type='button'
									onClick={onSelectAllElectionTypes}
								>
									Select All
								</button>
								<button
									type='button'
									onClick={onClearElectionTypes}
								>
									Clear
								</button>
							</div>
						</MultiSelectDropdown>
					)}
				</MultiSelectContainer>

				{/* Status Pill Group */}
				<StatusPillGroup
					role='radiogroup'
					aria-label='Election status'
				>
					{STATUS_OPTIONS.map((option) => (
						<StatusPill
							key={option.value ?? 'all'}
							type='button'
							className={filters.status === option.value ? 'active' : ''}
							onClick={handleStatusClick(option.value)}
							role='radio'
							aria-checked={filters.status === option.value}
						>
							{option.label}
						</StatusPill>
					))}
				</StatusPillGroup>

				{/* More Filters Toggle */}
				<MoreFiltersButton
					type='button'
					className={`${isAdvancedOpen ? 'open' : ''} ${advancedFilterCount > 0 ? 'has-filters' : ''}`.trim()}
					onClick={onToggleAdvanced}
					aria-expanded={isAdvancedOpen}
				>
					<SettingsIcon />
					More Filters
					{advancedFilterCount > 0 && <span className='badge'>{advancedFilterCount}</span>}
					<ChevronIcon />
				</MoreFiltersButton>
			</QuickFiltersRow>
		</FilterBarContainer>
	);
};
